import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  People as ContactsIcon
} from '@mui/icons-material';
import { contactService } from '../services/contactService';
import { companyService } from '../services/companyService';
import UserDisplay from '../components/UserDisplay';
import userService from '../services/userService';

const Contacts = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [contacts, setContacts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [contactToDelete, setContactToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  // Sorting state
  const [orderBy, setOrderBy] = useState('firstName');
  const [order, setOrder] = useState('asc');
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Filter state
  const [searchFilters, setSearchFilters] = useState({
    firstName: '',
    lastName: '',
    company: '',
    createdBy: '',
    createdDate: ''
  });

  const [userDisplayData, setUserDisplayData] = useState({});

  const loadCompanies = async () => {
    try {
      const companiesData = await companyService.getCompanies();
      setCompanies(companiesData);
    } catch (err) {
      console.error('Error loading companies:', err);
    }
  };

  const getCompanyName = useCallback((companyId) => {
    if (!companyId) return '';
    const company = companies.find(c => c.companyId === companyId);
    return company ? company.companyName : '';
  }, [companies]);

  const loadContacts = async (showLoadingIndicator = true) => {
    if (showLoadingIndicator) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }
    setError('');
    
    try {
      // Validate configuration first
      contactService.validateConfiguration();
      const contactsData = await contactService.getContacts();
      
      // contactsData is now an array of contacts in camelCase
      setContacts(contactsData);
    } catch (err) {
      console.error('Error loading contacts:', err);
      if (err.message.includes('Missing required environment variables')) {
        setError(`Configuration Error: ${err.message}. Please check your .env file.`);
      } else if (err.message.includes('Failed to fetch contacts: 404')) {
        // Try to initialize the file for first-time setup
        try {
          const initializedContacts = await contactService.initializeContactsFile();
          setContacts(initializedContacts);
        } catch (initError) {
          setError(`Failed to initialize contacts: ${initError.message}`);
        }
      } else if (err.message.includes('403') || err.message.includes('Server failed to authenticate')) {
        // Show a user-friendly message for authentication errors
        setError('Temporary authentication issue. Please try refreshing again in a moment.');
        console.warn('Contacts page: Authentication error - user should retry');
      } else {
        setError(`Failed to load contacts: ${err.message}`);
      }
    } finally {
      if (showLoadingIndicator) {
        setLoading(false);
      } else {
        setRefreshing(false);
      }
    }
  };

  // Load user display data for all contacts
  const loadUserDisplayData = useCallback(async () => {
    const userEmails = contacts
      .map(contact => contact.createdByEmail)
      .filter((email, index, self) => email && self.indexOf(email) === index);

    const displayData = {};
    for (const email of userEmails) {
      const userData = await userService.getUserByEmail(email);
      displayData[email] = userData;
    }
    setUserDisplayData(displayData);
  }, [contacts]);

  useEffect(() => {
    loadContacts();
    loadCompanies();
    loadUserDisplayData();
  }, []);

  // Auto-refresh functionality
  useEffect(() => {
    // Refresh data when window regains focus (user switches back to tab)
    const handleFocus = () => {
      loadContacts(true); // Force refresh
      loadCompanies();
    };

    // Refresh data every 2 minutes when tab is active
    const refreshInterval = setInterval(() => {
      if (!document.hidden) {
        loadContacts(false); // Background refresh
        loadCompanies();
      }
    }, 2 * 60 * 1000); // 2 minutes

    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
      clearInterval(refreshInterval);
    };
  }, []);

  const getUserDisplayName = useCallback((email) => {
    if (!email) return '';
    const userData = userDisplayData[email];
    return userData ? userData.name : email;
  }, [userDisplayData]);

  const applyFiltersAndSort = useCallback(() => {
    let filtered = [...contacts];

    // Apply filters
    Object.keys(searchFilters).forEach(key => {
      if (searchFilters[key]) {
        filtered = filtered.filter(contact => {
          if (key === 'company') {
            // Filter by company name
            const companyName = getCompanyName(contact.companyId).toLowerCase();
            return companyName.includes(searchFilters[key].toLowerCase());
          } else if (key === 'createdBy') {
            // Filter by user display name
            const displayName = getUserDisplayName(contact.createdByEmail).toLowerCase();
            return displayName.includes(searchFilters[key].toLowerCase());
          } else {
            const value = contact[key]?.toString().toLowerCase() || '';
            return value.includes(searchFilters[key].toLowerCase());
          }
        });
      }
    });

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue, bValue;
      
      if (orderBy === 'company') {
        aValue = getCompanyName(a.companyId).toLowerCase();
        bValue = getCompanyName(b.companyId).toLowerCase();
      } else if (orderBy === 'createdDate') {
        aValue = new Date(a[orderBy]);
        bValue = new Date(b[orderBy]);
      } else if (orderBy === 'createdBy') {
        // Sort by user display name
        aValue = getUserDisplayName(a.createdByEmail).toLowerCase();
        bValue = getUserDisplayName(b.createdByEmail).toLowerCase();
      } else {
        aValue = a[orderBy]?.toString().toLowerCase() || '';
        bValue = b[orderBy]?.toString().toLowerCase() || '';
      }

      if (order === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

    setFilteredContacts(filtered);
  }, [contacts, searchFilters, order, orderBy, getCompanyName, getUserDisplayName]);

  useEffect(() => {
    applyFiltersAndSort();
  }, [contacts, searchFilters, order, orderBy, applyFiltersAndSort]);

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleFilterChange = (field) => (event) => {
    setSearchFilters(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    setPage(0); // Reset to first page when filtering
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCreateContact = () => {
    navigate('/contact/new');
  };

  const handleEditContact = (contactId) => {
    navigate(`/contact/${contactId}`);
  };

  const handleDeleteClick = (contact) => {
    setContactToDelete(contact);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!contactToDelete) return;

    setDeleting(true);
    try {
      // Use contactId instead of Id
      const contactId = contactToDelete.contactId || contactToDelete.Id;
      
      if (!contactId) {
        throw new Error('Contact ID is undefined');
      }
      
      await contactService.deleteContact(contactId);
      await loadContacts(); // Reload the list
      setDeleteDialogOpen(false);
      setContactToDelete(null);
    } catch (err) {
      console.error('Failed to delete contact:', err);
      // Check if the error is due to associated opportunities
      if (err.message.includes('opportunities associated')) {
        setErrorMessage(err.message);
        setErrorDialogOpen(true);
      } else {
        setError(err.message);
      }
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setContactToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setContactToDelete(null);
  };

  const handleErrorDialogClose = () => {
    setErrorDialogOpen(false);
    setErrorMessage('');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const columns = isMobile 
    ? [
        { id: 'firstName', label: 'First Name', sortable: true },
        { id: 'lastName', label: 'Last Name', sortable: true },
        { id: 'company', label: 'Company', sortable: true },
        { id: 'actions', label: 'Actions', sortable: false }
      ]
    : [
        { id: 'firstName', label: 'First Name', sortable: true },
        { id: 'lastName', label: 'Last Name', sortable: true },
        { id: 'company', label: 'Company', sortable: true },
        { id: 'title', label: 'Title', sortable: true },
        { id: 'createdBy', label: 'Created By', sortable: true, field: 'createdByEmail' },
        { id: 'createdDate', label: 'Created Date', sortable: true },
        { id: 'actions', label: 'Actions', sortable: false }
      ];

  // Paginate the filtered results
  const paginatedContacts = filteredContacts.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box sx={{ width: '100%', pr: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h4">
            <ContactsIcon sx={{ mr: 2 }} />Contacts
          </Typography>
          {refreshing && (
            <Typography variant="body2" color="text.secondary">
              Refreshing data...
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Refresh Button */}
          <IconButton
            onClick={() => loadContacts(true)}
            disabled={loading || refreshing}
            title="Refresh data"
            sx={{ 
              color: 'primary.main',
              animation: refreshing ? 'spin 1s linear infinite' : 'none',
              '@keyframes spin': {
                '0%': {
                  transform: 'rotate(0deg)',
                },
                '100%': {
                  transform: 'rotate(360deg)',
                },
              }
            }}
          >
            <RefreshIcon />
          </IconButton>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateContact}
          >
            Create Contact
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper elevation={3}>
        <TableContainer>
          <Table>
            <TableHead>
              {/* Filter Row */}
              <TableRow key="filter-row">
                {columns.map((column) => (
                  <TableCell key={`filter-${column.id}`}>
                    {column.id !== 'actions' && (
                      <TextField
                        size="small"
                        placeholder={`Search ${column.label}`}
                        value={searchFilters[column.id] || ''}
                        onChange={handleFilterChange(column.id)}
                        InputProps={{
                          startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        }}
                        sx={{ minWidth: isMobile ? '120px' : '150px' }}
                      />
                    )}
                  </TableCell>
                ))}
              </TableRow>
              
              {/* Header Row */}
              <TableRow key="header-row">
                {columns.map((column) => (
                  <TableCell key={column.id}>
                    {column.sortable ? (
                      <TableSortLabel
                        active={orderBy === column.id}
                        direction={orderBy === column.id ? order : 'asc'}
                        onClick={() => handleSort(column.id)}
                      >
                        {column.label}
                      </TableSortLabel>
                    ) : (
                      column.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedContacts.map((contact) => (
                <TableRow key={contact.contactId || `contact-${contact.contactId}`} hover>
                  <TableCell>{contact.firstName}</TableCell>
                  <TableCell>{contact.lastName}</TableCell>
                  {isMobile ? (
                    <>
                      <TableCell align="center">
                        {getCompanyName(contact.companyId) && (
                          <Button
                            variant="text"
                            size="small"
                            onClick={() => navigate(`/company/${contact.companyId}`)}
                            sx={{ textTransform: 'none', p: 0, minWidth: 'auto' }}
                          >
                            {getCompanyName(contact.companyId)}
                          </Button>
                        )}
                      </TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell align="center">
                        {getCompanyName(contact.companyId) && (
                          <Button
                            variant="text"
                            size="small"
                            onClick={() => navigate(`/company/${contact.companyId}`)}
                            sx={{ textTransform: 'none', p: 0, minWidth: 'auto' }}
                          >
                            {getCompanyName(contact.companyId)}
                          </Button>
                        )}
                      </TableCell>
                      <TableCell>{contact.title}</TableCell>
                      <TableCell>
                        <UserDisplay email={contact.createdByEmail} size="medium" />
                      </TableCell>
                      <TableCell>{new Date(contact.createdDate).toLocaleString()}</TableCell>
                    </>
                  )}
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleEditContact(contact.contactId)}
                        title="Edit Contact"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteClick(contact)}
                        title="Delete Contact"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {paginatedContacts.length === 0 && (
                <TableRow key="empty-state">
                  <TableCell colSpan={columns.length} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      {filteredContacts.length === 0 && contacts.length > 0
                        ? 'No contacts match your search criteria'
                        : 'No contacts found. Create your first contact to get started.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {filteredContacts.length > 0 && (
          <TablePagination
            component="div"
            count={filteredContacts.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
          />
        )}
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the contact "{contactToDelete?.firstName} {contactToDelete?.lastName}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : null}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Dialog for Association Errors */}
      <Dialog open={errorDialogOpen} onClose={handleErrorDialogClose}>
        <DialogTitle>Cannot Delete Contact</DialogTitle>
        <DialogContent>
          <Typography color="error">
            {errorMessage}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleErrorDialogClose} variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Contacts;
